import { Sidebar } from '@hopskipdrive/react-components';
import { alpha } from '@mui/material/styles';
import { colorPalette } from '@rideiq/shared/components/variables';
import styled, { css } from 'styled-components';

const sidebarItemWidth = 74;
const sidebarItemMinHeight = 64;
const sidebarItemRadius = 14;
const sidebarItemVerticalMargin = 4;
const sidebarRestingForeground = colorPalette.navy99;
const sidebarSelectedBackground = alpha(colorPalette.neutral100, 0.36);
const sidebarSelectedForeground = colorPalette.neutral100;

export const sidebarFocusVisibleStyles = css`
  outline: 2px solid ${colorPalette.neutral100};
  outline-offset: 2px;
`;

interface SidebarNavMenuLinkItemProps {
  $mobile?: boolean;
  $truncateLabel?: boolean;
}

interface SidebarNavContainerProps {
  $mobile?: boolean;
}

export const SidebarNavMenuLinkItem = styled(Sidebar.MenuLinkItem)<SidebarNavMenuLinkItemProps>`
  &&,
  &&:hover {
    box-sizing: border-box;
    width: ${sidebarItemWidth}px;
    min-width: 0;
    max-width: calc(100% - 16px);
    min-height: ${sidebarItemMinHeight}px;
    flex-shrink: 0;
    margin: ${sidebarItemVerticalMargin}px auto;
    padding: 4px 2px;
    gap: 2px;
    border-radius: ${sidebarItemRadius}px;
    background-color: ${({ active }) => (active ? sidebarSelectedBackground : 'transparent')};
    color: ${({ active }) => (active ? sidebarSelectedForeground : sidebarRestingForeground)};
    text-decoration: none;
  }

  && ${Sidebar.MenuLinkText}, &&:hover ${Sidebar.MenuLinkText} {
    color: inherit;
    opacity: 1;
    font-weight: ${({ active }) => (active ? 500 : 400)};
    text-decoration: none;
  }

  && ${Sidebar.MenuLinkText} {
    max-width: 100%;
    white-space: normal;
    overflow: visible;
    text-align: center;
    overflow-wrap: anywhere;
  }

  &&:hover ${Sidebar.MenuLinkText} {
    text-decoration: ${({ active }) => (active ? 'none' : 'underline')};
  }

  &&:focus-visible {
    ${sidebarFocusVisibleStyles}
  }

  ${({ $truncateLabel }) =>
    $truncateLabel &&
    css`
      && ${Sidebar.MenuLinkText} {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `}

  ${({ $mobile }) =>
    $mobile &&
    css`
      &&,
      &&:hover {
        width: 100%;
        max-width: 100%;
        min-height: 0;
        margin: 0;
        padding: 20px;
        flex-direction: row;
        justify-content: flex-start;
        gap: 12px;
      }

      && ${Sidebar.MenuLinkText} {
        text-align: left;
        min-width: 0;
      }
    `}
`;

export const SidebarNavContainer = styled.div<SidebarNavContainerProps>`
  box-sizing: border-box;
  width: 90px;
  min-width: 90px;
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  scroll-padding-block: 4px;
  display: flex;
  flex-direction: column;

  ${({ $mobile }) =>
    !$mobile &&
    css`
      /* Scrollbars must not reduce the 90px rail's available tile width. */
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    `}

  ${({ $mobile }) =>
    $mobile &&
    css`
      width: 100%;
      min-width: 0;
      height: 100%;
      /* Keep the outward focus outline inside the scroll area. */
      padding: 4px;
    `}
`;

export const SidebarNavMenu = styled(Sidebar.Menu)`
  min-width: 0;
  flex: 1 0 auto;
  overflow: visible;
`;

export const TopDivider = styled.hr`
  width: 50px;
  height: 1px;
  margin-top: 0.4rem;
  margin-bottom: 12px;
  background-color: ${colorPalette.navy90};
`;

export const BottomDivider = styled.hr`
  width: 50px;
  height: 1px;
  margin-top: 0.4rem;
  margin-bottom: 12px;
  align-self: center;
  opacity: 0.1;
  background-color: ${colorPalette.navy90};
`;
